import React, { Suspense } from "react";
import { createBrowserRouter, createHashRouter, Outlet, Navigate, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { ArrowLeft, Home, OctagonAlert, Radar, ShieldX } from "lucide-react";
import { Capacitor } from "@capacitor/core";
import { PWAInstallPrompt } from "./components/pwa-install-prompt";
import { RouteErrorBoundary } from "./components/route-error-boundary";
import { Toaster } from "./components/ui/sonner";
import { ConfigurationPortal } from "./components/configuration/configuration-portal";
import { ConfigurationEntry } from "./components/configuration/configuration-entry";
import { DemoAccessGate } from "./components/demo-access-gate";
import { DemoExperienceTour } from "./components/demo-experience-tour";
import { VisionMobileApp } from "./components/mobile/vision-mobile-app";
import { VisionAlerts } from "./components/vision-alerts";
import { VisionDemo } from "./components/vision-demo";
import { VisionGrowthMonitor } from "./components/vision-growth-monitor";
import { AqlVisionMark } from "./components/branding/aql-vision-brand";
import { AqlVisionLoader } from "./components/branding/aql-vision-loader";
import { useAppIdentity } from "./hooks/use-app-identity";
import { useGlobal, type ClientScope } from "./providers/global-provider";
import { supabase } from "@/utils/supabase/client";
import { aqlVisionApiUrl, hasSupabaseConfig, publicAnonKey } from "/utils/supabase/info";

const slugify = (value: string) =>
  value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '');

const slugSegments = (value: unknown) =>
  String(value ?? '')
    .split(/[/?#]+/)
    .map((segment) => slugify(segment))
    .filter(Boolean);

const projectMatchesRoute = (row: any, routeProjectSlug: string, routeClientSlug: string) => {
  const rawSlug = String(row.slug ?? '');
  const slugParts = slugSegments(rawSlug);
  const directSlug = slugify(rawSlug);
  const nameSlug = slugify(String(row.name ?? ''));

  return (
    directSlug === routeProjectSlug ||
    directSlug === `${routeClientSlug}-${routeProjectSlug}` ||
    slugParts[slugParts.length - 1] === routeProjectSlug ||
    (slugParts[0] === routeClientSlug && slugParts[slugParts.length - 1] === routeProjectSlug) ||
    nameSlug === routeProjectSlug
  );
};

type ClientRouteStatus =
  | { state: 'checking' }
  | { state: 'found' }
  | { state: 'dead-end'; slug: string; segment: 'client' | 'project' | 'validation' };

const clientScopeFromRow = (row: any): ClientScope => ({
  id: String(row.client_id ?? ''),
  name: String(row.name ?? ''),
  slug: String(row.slug ?? ''),
  logo_url: row.logo_url ? String(row.logo_url) : undefined,
  primary_color: row.primary_color ? String(row.primary_color) : undefined,
  secondary_color: row.secondary_color ? String(row.secondary_color) : undefined,
  species_focus: row.species_focus ? String(row.species_focus) : undefined,
  default_phase: row.default_phase ? String(row.default_phase) as ClientScope['default_phase'] : undefined
});

function VisionDeadEnd({ slug, segment, onBack }: { slug: string; segment: 'client' | 'project' | 'validation'; onBack: () => void }) {
  const headline = segment === 'validation'
    ? 'DEAD END / SINAL NAO VALIDADO'
    : 'DEAD END / SLUG INEXISTENTE';
  const detail = segment === 'project'
    ? 'O projeto pedido nao existe para este cliente. O ambiente de producao ficou fechado.'
    : segment === 'validation'
      ? 'Nao foi possivel validar este endpoint no catalogo operacional. O ambiente de producao ficou fechado.'
      : 'Este cliente nao existe no catalogo operacional. O ambiente de producao ficou fechado.';

  return (
    <main className="relative min-h-screen overflow-hidden bg-[#061017] text-white">
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_50%_32%,rgba(56,189,248,0.22),rgba(6,16,23,0)_32%),linear-gradient(180deg,rgba(2,6,23,0.12),rgba(2,6,23,0.74))]" />
      <div className="pointer-events-none absolute inset-0 opacity-[0.16] [background-image:linear-gradient(rgba(125,211,252,0.18)_1px,transparent_1px),linear-gradient(90deg,rgba(251,146,60,0.12)_1px,transparent_1px)] [background-size:74px_74px]" />
      <div className="pointer-events-none absolute inset-x-0 top-[18%] h-px bg-gradient-to-r from-transparent via-cyan-200/70 to-transparent" />
      <div className="pointer-events-none absolute inset-x-0 bottom-[22%] h-px bg-gradient-to-r from-transparent via-orange-300/60 to-transparent" />

      <section className="relative z-10 flex min-h-screen items-center justify-center px-5 py-12">
        <div className="w-full max-w-3xl text-center">
          <div className="mx-auto mb-8 flex h-28 w-28 items-center justify-center rounded-full border border-cyan-200/20 bg-cyan-300/8 shadow-[0_0_80px_rgba(56,189,248,0.22)] backdrop-blur">
            <AqlVisionMark className="h-16 w-16 text-cyan-100" alt="AQL Vision" />
          </div>

          <div className="mb-5 inline-flex items-center gap-2 rounded-full border border-orange-300/30 bg-orange-400/10 px-4 py-2 text-xs font-bold uppercase tracking-[0.22em] text-orange-100">
            <OctagonAlert className="h-4 w-4" />
            Endpoint bloqueado
          </div>

          <h1 className="text-balance text-4xl font-black uppercase leading-[0.92] tracking-normal text-white drop-shadow-[0_0_42px_rgba(14,165,233,0.3)] sm:text-6xl">
            {headline}
          </h1>

          <div className="mx-auto my-7 h-px w-[min(72vw,460px)] bg-gradient-to-r from-transparent via-cyan-200/70 to-transparent" />

          <p className="mx-auto max-w-2xl text-base leading-7 text-cyan-50/76 sm:text-lg">
            {detail}
          </p>

          <div className="mx-auto mt-7 flex max-w-xl flex-col items-stretch justify-center gap-3 sm:flex-row">
            <div className="flex min-w-0 items-center justify-center gap-2 rounded-xl border border-cyan-200/20 bg-white/[0.035] px-4 py-3 font-mono text-sm text-cyan-50/86">
              <Radar className="h-4 w-4 flex-none text-cyan-200" />
              <span className="truncate">/{slug || 'unknown'}</span>
            </div>
            <div className="flex min-w-0 items-center justify-center gap-2 rounded-xl border border-orange-300/22 bg-orange-400/8 px-4 py-3 text-sm font-semibold text-orange-50/86">
              <ShieldX className="h-4 w-4 flex-none text-orange-200" />
              Producao nao iniciada
            </div>
          </div>

          <div className="mt-9 flex flex-col justify-center gap-3 sm:flex-row">
            <button
              type="button"
              onClick={onBack}
              className="inline-flex items-center justify-center gap-2 rounded-xl border border-cyan-200/28 bg-cyan-300/12 px-5 py-3 text-sm font-semibold text-cyan-50 transition-colors hover:border-cyan-100/50 hover:bg-cyan-300/20"
            >
              <ArrowLeft className="h-4 w-4" />
              Voltar
            </button>
            <button
              type="button"
              onClick={onBack}
              className="inline-flex items-center justify-center gap-2 rounded-xl border border-white/12 bg-white/[0.045] px-5 py-3 text-sm font-semibold text-white/76 transition-colors hover:border-white/22 hover:bg-white/[0.08] hover:text-white"
            >
              <Home className="h-4 w-4" />
              Entrada AQL Vision
            </button>
          </div>
        </div>
      </section>
    </main>
  );
}

function ClientDashboardWrapper() {
  useAppIdentity('vision');
  const navigate = useNavigate();
  const params = useParams<{ clientSlug?: string; projectSlug?: string }>();
  const { setAvailableClients, setClient } = useGlobal();
  const [routeStatus, setRouteStatus] = React.useState<ClientRouteStatus>({ state: 'checking' });

  React.useEffect(() => {
    const rawClientSlug = params.clientSlug ?? '';
    const routeClientSlug = slugify(rawClientSlug);
    const routeProjectSlug = params.projectSlug ? slugify(params.projectSlug) : null;

    if (!routeClientSlug || !hasSupabaseConfig) {
      setClient(null);
      setAvailableClients([]);
      setRouteStatus({ state: 'dead-end', slug: rawClientSlug, segment: 'validation' });
      return;
    }

    let active = true;
    setRouteStatus({ state: 'checking' });

    const resolveClientRoute = async () => {
      try {
        const { data: routeRows, error: routeResolverError } = await supabase
          .rpc('aql_resolve_public_vision_route', {
            p_client_slug: routeClientSlug,
            p_project_slug: routeProjectSlug
          });

        const routeRow = Array.isArray(routeRows) ? routeRows[0] : routeRows;
        if (!routeResolverError && routeRow) {
          if (!routeRow.found) {
            setClient(null);
            setAvailableClients([]);
            setRouteStatus({
              state: 'dead-end',
              slug: routeProjectSlug ? `${rawClientSlug}/${params.projectSlug ?? ''}` : rawClientSlug,
              segment: routeRow.failure_segment === 'project' ? 'project' : 'client'
            });
            return;
          }

          const matchedClient = clientScopeFromRow(routeRow);
          setAvailableClients([matchedClient]);
          setClient(matchedClient);
          setRouteStatus({ state: 'found' });
          return;
        }

        const { data: clientRows, error: clientError } = await supabase
          .from('aql_clients')
          .select('client_id,name,slug,logo_url,primary_color,secondary_color')
          .eq('is_active', true)
          .order('name');

        if (clientError) throw clientError;

        const matchedClientRow = Array.isArray(clientRows)
          ? clientRows.find((row: any) =>
            slugify(String(row.slug ?? '')) === routeClientSlug ||
            slugify(String(row.name ?? '')) === routeClientSlug
          )
          : null;

        if (!active) return;

        if (!matchedClientRow) {
          setClient(null);
          setAvailableClients([]);
          setRouteStatus({ state: 'dead-end', slug: rawClientSlug, segment: 'client' });
          return;
        }

        if (routeProjectSlug) {
          const { data: projectRows, error: projectError } = await supabase
            .from('aql_projects')
            .select('project_id,name,slug')
            .eq('is_active', true)
            .eq('client_id', matchedClientRow.client_id);

          if (projectError) throw projectError;

          const matchedProject = Array.isArray(projectRows)
            ? projectRows.find((row: any) => projectMatchesRoute(row, routeProjectSlug, routeClientSlug))
            : null;

          if (!active) return;

          if (!matchedProject) {
            setClient(null);
            setAvailableClients([]);
            setRouteStatus({ state: 'dead-end', slug: `${rawClientSlug}/${params.projectSlug ?? ''}`, segment: 'project' });
            return;
          }
        }

        const matchedClient = clientScopeFromRow(matchedClientRow);
        setAvailableClients([matchedClient]);
        setClient(matchedClient);
        setRouteStatus({ state: 'found' });
      } catch (error) {
        console.error('Failed to validate AQL Vision route slug:', error);
        if (!active) return;
        setClient(null);
        setAvailableClients([]);
        setRouteStatus({ state: 'dead-end', slug: rawClientSlug, segment: 'validation' });
      }
    };

    resolveClientRoute();

    return () => {
      active = false;
    };
  }, [params.clientSlug, params.projectSlug, setAvailableClients, setClient]);

  if (routeStatus.state === 'checking') {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#061017] px-4 text-white">
        <AqlVisionLoader
          label="A validar endpoint AQL Vision..."
          detail="A confirmar o slug antes de abrir producao."
        />
      </div>
    );
  }

  if (routeStatus.state === 'dead-end') {
    return (
      <VisionDeadEnd
        slug={routeStatus.slug}
        segment={routeStatus.segment}
        onBack={() => navigate('/')}
      />
    );
  }

  return (
    <VisionAlerts
      apiUrl={aqlVisionApiUrl}
      publicAnonKey={publicAnonKey}
      onBack={() => navigate('/')}
    />
  );
}

function VisionClientCameraWrapper() {
  useAppIdentity('vision');
  const navigate = useNavigate();
  const params = useParams<{ clientSlug?: string; projectSlug?: string }>();
  const [searchParams] = useSearchParams();
  const { client } = useGlobal();
  const [resolvedClientId, setResolvedClientId] = React.useState<string | null>(client?.id ?? null);
  const [resolvedProjectId, setResolvedProjectId] = React.useState<string | null>(null);

  const dashboardPath = params.projectSlug
    ? `/${params.clientSlug ?? ''}/${params.projectSlug}`
    : `/${params.clientSlug ?? ''}`;
  const scopedClientId = params.projectSlug && !resolvedProjectId ? null : resolvedClientId;

  React.useEffect(() => {
    const routeClientSlug = params.clientSlug ? slugify(params.clientSlug) : null;
    const clientMatchesRoute = client && routeClientSlug
      ? slugify(client.slug) === routeClientSlug || slugify(client.name) === routeClientSlug
      : Boolean(client?.id);
    if (client?.id && clientMatchesRoute) {
      setResolvedClientId(client.id);
    } else if (params.clientSlug) {
      setResolvedClientId(null);
    }
    if (!params.clientSlug || !hasSupabaseConfig) {
      setResolvedProjectId(null);
      return;
    }
    let active = true;
    (async () => {
      try {
        const { data, error } = await supabase.rpc('aql_resolve_public_vision_route', {
          p_client_slug: params.clientSlug,
          p_project_slug: params.projectSlug ?? null
        });
        if (!active || error) return;
        const row = Array.isArray(data) ? data[0] : data;
        if (row?.client_id) setResolvedClientId(String(row.client_id));
        setResolvedProjectId(row?.project_id ? String(row.project_id) : null);
      } catch {
        // Silent fallback: VisionDemo will show the "no data" state.
        if (active) setResolvedProjectId(null);
      }
    })();
    return () => {
      active = false;
    };
  }, [client?.id, client?.name, client?.slug, params.clientSlug, params.projectSlug]);

  return (
    <VisionDemo
      onBack={() => navigate(dashboardPath)}
      onOpenAlerts={() => navigate(dashboardPath)}
      initialAlertId={searchParams.get('alertId')}
      demoMode={false}
      clientId={scopedClientId}
      projectId={resolvedProjectId}
    />
  );
}

function ConfigurationAdminWrapper() {
  useAppIdentity('vision');
  return <ConfigurationPortal forcedMode="admin" />;
}

function NativeAwareEntryWrapper() {
  useAppIdentity('vision');
  if (Capacitor.isNativePlatform()) {
    return <Navigate to="/mobile" replace />;
  }
  return <ConfigurationEntry />;
}

function VisionDemoWrapper() {
  useAppIdentity('vision');
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  return (
    <VisionDemo
      onBack={() => navigate('/demo')}
      onOpenAlerts={(alertId) => navigate(alertId ? `/demo/alerts?alertId=${encodeURIComponent(alertId)}` : '/demo/alerts')}
      initialAlertId={searchParams.get('alertId')}
    />
  );
}

function VisionDemoDashboardWrapper({ initialView = 'operations' }: { initialView?: 'operations' | 'clinical' }) {
  useAppIdentity('vision');
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  return (
    <VisionAlerts
      apiUrl={aqlVisionApiUrl}
      publicAnonKey={publicAnonKey}
      onBack={() => navigate('/')}
      demoMode
      demoInitialView={initialView}
      demoSelectedAlertId={searchParams.get('alertId')}
    />
  );
}

function VisionDemoAlertsWrapper() {
  return <VisionDemoDashboardWrapper initialView="clinical" />;
}

function VisionMobileWrapper() {
  useAppIdentity('vision');
  return <VisionMobileApp />;
}

function VisionMobileDemoWrapper() {
  useAppIdentity('vision');
  return <VisionMobileApp demoMode />;
}

function VisionGrowthMonitorWrapper() {
  useAppIdentity('vision');
  const navigate = useNavigate();
  return <VisionGrowthMonitor onBack={() => navigate('/')} />;
}

function DemoAccessLayout() {
  return (
    <DemoAccessGate>
      <Outlet />
      <DemoExperienceTour />
    </DemoAccessGate>
  );
}

function StandaloneLayout() {
  return (
    <div>
      <Suspense fallback={
        <div className="min-h-screen bg-slate-950 flex flex-col items-center justify-center text-white p-4">
          <AqlVisionLoader label="" />
          <div className="mt-8 text-center animate-pulse">
            <h1 className="text-3xl font-black tracking-tighter mb-2 bg-gradient-to-r from-white to-slate-400 bg-clip-text text-transparent italic">AQL VISION</h1>
            <p className="text-slate-500 text-[10px] uppercase tracking-[0.3em]">Qualidade & Monitorização Industrial</p>
          </div>
        </div>
      }>
        <Toaster position="top-center" expand={false} richColors />
        <PWAInstallPrompt />
        <Outlet />
      </Suspense>
    </div>
  );
}

const createStandaloneRouter = window.location.protocol === "file:" ? createHashRouter : createBrowserRouter;

// Simplified standalone router
export const router = createStandaloneRouter([
  {
    path: "/",
    Component: StandaloneLayout,
    errorElement: <RouteErrorBoundary />,
    children: [
      {
        index: true,
        Component: NativeAwareEntryWrapper
      },
      {
        path: "mobile",
        Component: VisionMobileWrapper
      },
      {
        path: "mobile/demo",
        Component: VisionMobileDemoWrapper
      },
      {
        path: "admin",
        Component: ConfigurationAdminWrapper
      },
      {
        path: "growth",
        Component: VisionGrowthMonitorWrapper
      },
      {
        path: "demo",
        Component: DemoAccessLayout,
        children: [
          {
            index: true,
            Component: VisionDemoDashboardWrapper
          },
          {
            path: "alerts",
            Component: VisionDemoAlertsWrapper
          },
          {
            path: "camera",
            Component: VisionDemoWrapper
          }
        ]
      },
      {
        path: ":clientSlug/camera",
        Component: VisionClientCameraWrapper
      },
      {
        path: ":clientSlug/:projectSlug/camera",
        Component: VisionClientCameraWrapper
      },
      {
        path: ":clientSlug",
        Component: ClientDashboardWrapper
      },
      {
        path: ":clientSlug/:projectSlug",
        Component: ClientDashboardWrapper
      },
      {
        path: "*",
        element: <Navigate to="/" replace />
      }
    ]
  }
]);
